import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FRANCHISE_TIERS, PAYMENT_OPTIONS } from "../lib/franchise";
import { api, formatApiErrorDetail } from "../lib/api";
import { PACKAGE_FOR_FRANCHISE } from "../lib/payments";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

const TIER_OPTIONS = [
  { id: "starter", label: "WealthFirst Starter" },
  { id: "grow_rich", label: "WealthFirst Grow-Rich" },
  { id: "enterprise", label: "WealthFirst Enterprise" },
];

function fmt(n) {
  return `$${n.toLocaleString()}`;
}

export default function FranchiseCheckout() {
  const [params] = useSearchParams();
  const initialTier = TIER_OPTIONS.find((t) => t.id === params.get("tier"))?.id || "grow_rich";

  const [form, setForm] = useState({
    tier: initialTier,
    payment_option: initialTier === "starter" ? "n/a" : "upfront",
    business_name: "",
    owner_name: "",
    email: "",
    phone: "",
    website: "",
    territory: "",
    experience: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const tier = FRANCHISE_TIERS[form.tier];

  // Live quote from backend (source of truth)
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    api.get("/franchise/quote", { params: { tier: form.tier, payment_option: form.payment_option } })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [form.tier, form.payment_option]);

  // Reset payment_option to "n/a" when switching to Starter (no installment plan on Starter)
  useEffect(() => {
    if (form.tier === "starter" && form.payment_option !== "n/a") {
      set("payment_option", "n/a");
    } else if (form.tier !== "starter" && form.payment_option === "n/a") {
      set("payment_option", "upfront");
    }
  }, [form.tier, form.payment_option]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.business_name.trim().length < 2) {
      toast.error("Please enter your franchise business name.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/franchise/apply", form);
      setDone(data);
      toast.success(`Welcome aboard, ${data.business_name}.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const showPaymentOptions = form.tier !== "starter";

  if (done) {
    const packageId =
      PACKAGE_FOR_FRANCHISE[form.tier]?.[form.payment_option] || PACKAGE_FOR_FRANCHISE[form.tier]?.["n/a"];
    return (
      <div data-testid="franchise-applied" className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-10">
          <CheckCircle2 size={56} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Application Received · Complete Payment</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            Welcome, <em style={{ color: "var(--brass)" }}>{done.business_name}</em>.
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>
            Reserve your tier by paying the setup fee of{" "}
            <strong style={{ color: "var(--brass)" }}>{fmt(done.total_due_today)}</strong>. Choose any rail —
            fiat, stablecoin, native crypto, or on-chain Web3 wallet.
          </p>
        </div>
        <div className="p-6 mb-8" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}>
          <p className="eyebrow mb-3">Application Summary</p>
          <div className="space-y-2 text-sm">
            <Row label="Business" value={done.business_name} />
            <Row label="Tier" value={tier.name} />
            <Row label="Setup due today" value={fmt(done.setup_fee_due_today)} highlight />
            <Row label="Monthly recurring" value={fmt(done.monthly_fee)} />
            <Row label="Total at checkout" value={fmt(done.total_due_today)} highlight bold />
          </div>
        </div>
        <PaymentMethodSelector
          packageId={packageId}
          amount={done.setup_fee_due_today}
          label={`${tier.name} — Setup`}
          metadata={{ franchise_id: done.id, business_name: done.business_name, tier: done.tier }}
        />
      </div>
    );
  }

  return (
    <div data-testid="franchise-checkout-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-12 reveal">
        <p className="eyebrow mb-4">Become a WEALTH FIRST INC. Franchise Partner</p>
        <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
          Checkout & <em style={{ color: "var(--brass)" }}>Onboarding</em>.
        </h1>
        <p className="text-base max-w-2xl" style={{ color: "var(--ink-muted)" }}>
          Choose your tier, name your practice, and select how you would like to handle the setup fee.
          A partner advisor will follow up within one business day with documents and the live payment link.
        </p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="franchise-form">
        {/* Left — Form fields */}
        <div className="lg:col-span-7 space-y-8">
          {/* Tier */}
          <section>
            <p className="eyebrow mb-3">1 · Choose Your Tier</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {TIER_OPTIONS.map((t) => (
                <label
                  key={t.id}
                  data-testid={`tier-option-${t.id}`}
                  className="p-4 cursor-pointer transition-all flex flex-col"
                  style={{
                    border: form.tier === t.id ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                    background: form.tier === t.id ? "var(--surface)" : "transparent",
                  }}
                >
                  <input type="radio" name="tier" value={t.id} checked={form.tier === t.id} onChange={() => set("tier", t.id)} className="sr-only" />
                  <p className="eyebrow mb-2" style={{ color: form.tier === t.id ? "var(--brass)" : "var(--ink-muted)" }}>
                    {t.label.replace("WealthFirst ", "")}
                  </p>
                  <p className="font-display text-2xl" style={{ color: "var(--brass)" }}>
                    {FRANCHISE_TIERS[t.id].setupDisplay}
                  </p>
                  <p className="text-xs uppercase tracking-[0.18em] mt-1" style={{ color: "var(--ink-faint)" }}>
                    + {FRANCHISE_TIERS[t.id].monthlyDisplay}
                  </p>
                </label>
              ))}
            </div>
          </section>

          {/* Payment Option (Grow-Rich + Enterprise only) */}
          {showPaymentOptions && (
            <section>
              <p className="eyebrow mb-3">2 · Setup-Fee Payment Plan</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.values(PAYMENT_OPTIONS).map((opt) => (
                  <label
                    key={opt.id}
                    data-testid={`payment-option-${opt.id}`}
                    className="p-5 cursor-pointer transition-all"
                    style={{
                      border: form.payment_option === opt.id ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                      background: form.payment_option === opt.id ? "var(--surface)" : "transparent",
                    }}
                  >
                    <input type="radio" name="payment_option" value={opt.id} checked={form.payment_option === opt.id} onChange={() => set("payment_option", opt.id)} className="sr-only" />
                    <p className="text-sm font-semibold uppercase tracking-[0.15em] mb-2" style={{ color: form.payment_option === opt.id ? "var(--brass)" : "var(--ink)" }}>
                      {opt.label}
                    </p>
                    <p className="font-display text-2xl mb-2" style={{ color: "var(--brass)" }}>{opt.setupDisplay}</p>
                    <p className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--ink-muted)" }}>{opt.note}</p>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--ink-muted)" }}>{opt.schedule}</p>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Business Info */}
          <section>
            <p className="eyebrow mb-3">{showPaymentOptions ? "3" : "2"} · Your Practice</p>
            <div className="space-y-6">
              <div>
                <label className="eyebrow block mb-2">Franchise Business Name *</label>
                <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="e.g. Beaumont Capital Advisors LLC" className="input-line" data-testid="franchise-business-name" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow block mb-2">Owner Name *</label>
                  <input required value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="input-line" data-testid="franchise-owner-name" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" data-testid="franchise-email" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Phone</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-line" data-testid="franchise-phone" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Website (optional)</label>
                  <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="input-line" data-testid="franchise-website" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Territory / Region</label>
                  <input value={form.territory} onChange={(e) => set("territory", e.target.value)} placeholder="e.g. Greater Atlanta · Southeast US" className="input-line" data-testid="franchise-territory" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Industry Experience</label>
                  <input value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 8 years in CRE syndication" className="input-line" data-testid="franchise-experience" />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2">Anything else we should know?</label>
                <textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Existing client base, partnerships, target verticals, timeline." className="w-full bg-transparent p-3 text-base outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="franchise-notes" />
              </div>
            </div>
          </section>
        </div>

        {/* Right — Sticky billing summary */}
        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start" data-testid="franchise-summary">
          <div className="p-8" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }}>
            <p className="eyebrow mb-3">Due Today at Checkout</p>
            <h3 className="font-display text-3xl mb-1">{tier.name}</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ink-muted)" }}>{tier.tagline}</p>

            <div className="space-y-3 mb-6">
              <Row label="Setup fee" value={quote ? fmt(quote.setup_fee_due_today) : tier.setupDisplay} />
              <Row label="Monthly fee" value={quote ? fmt(quote.monthly_fee) : tier.monthlyDisplay.replace(" / month", "")} />
              <Row label="Client capacity" value={tier.clientLimit ? tier.clientLimit.toString() : "Unlimited"} />
            </div>

            <hr className="hairline mb-5" />

            <div className="mb-7">
              <p className="eyebrow mb-2">Total Due Today</p>
              <p className="font-display text-5xl" style={{ color: "var(--brass)" }}>
                {quote ? fmt(quote.total_due_today) : "—"}
              </p>
              {showPaymentOptions && form.payment_option === "installments" && (
                <p className="text-xs uppercase tracking-[0.18em] mt-2" style={{ color: "var(--ink-muted)" }}>
                  First of 4 setup installments + first monthly
                </p>
              )}
            </div>

            <button type="submit" disabled={busy} className="btn-brass w-full" style={{ justifyContent: "center" }} data-testid="franchise-submit">
              {busy ? "Submitting…" : `Apply & Reserve ${tier.name.replace("WealthFirst ", "")}`}
              <ArrowRight size={14} />
            </button>

            <p className="text-xs mt-5 leading-relaxed text-center" style={{ color: "var(--ink-faint)" }}>
              Payment is collected only after your franchise documents are signed.
              A partner advisor will follow up within one business day.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}

function Row({ label, value, highlight, bold }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>{label}</span>
      <span
        className={bold ? "font-display text-2xl" : "text-sm"}
        style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}
      >
        {value}
      </span>
    </div>
  );
}
